/**
 * Canonical EMA stack (20 / 50 / 200) — single source for gate, scoring, UI, CSV.
 */

export function emaSeries(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  const chrono = [...closes].reverse();
  let ema = chrono.slice(0, period).reduce((s, v) => s + v, 0) / period;
  const k = 2 / (period + 1);
  const outChrono = [ema];
  for (let i = period; i < chrono.length; i++) {
    ema = chrono[i] * k + ema * (1 - k);
    outChrono.push(ema);
  }
  return [...outChrono].reverse();
}

export function emaLatest(closes, period) {
  const s = emaSeries(closes, period);
  return s?.[0] ?? null;
}

/** Simple moving average (latest bar) — legacy comparison only. */
export function smaLatest(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  const slice = closes.slice(0, period);
  return slice.reduce((s, v) => s + v, 0) / period;
}

function pctFrom(price, level) {
  if (price == null || level == null || level === 0) return null;
  return ((price - level) / level) * 100;
}

function slopePct(series, days = 5) {
  if (!series?.length || series.length <= days) return null;
  const a = series[0];
  const b = series[days];
  if (a == null || b == null || b === 0) return null;
  return ((a - b) / b) * 100;
}

/** Last golden or death cross of EMA50 vs EMA200 within lookback daily bars. */
export function detectLastEmaCross(closes, lookback = 120) {
  if (!closes?.length || closes.length < 205) return null;
  const e50 = emaSeries(closes, 50);
  const e200 = emaSeries(closes, 200);
  if (!e50 || !e200) return null;
  const n = Math.min(lookback, closes.length - 1);
  for (let i = 0; i < n; i++) {
    const d0 = e50[i] - e200[i];
    const d1 = e50[i + 1] - e200[i + 1];
    if (d0 == null || d1 == null || !Number.isFinite(d0) || !Number.isFinite(d1)) continue;
    if (d0 > 0 && d1 <= 0) {
      return { type: "GOLDEN", barsAgo: i };
    }
    if (d0 < 0 && d1 >= 0) {
      return { type: "DEATH", barsAgo: i };
    }
  }
  return null;
}

export function crossToDateIso(closes, barsAgo) {
  if (barsAgo == null || !closes?.length) return null;
  const d = new Date();
  d.setDate(d.getDate() - barsAgo);
  return d.toISOString().slice(0, 10);
}

/**
 * Full MA payload for scan result + UI.
 * @param {number[]} closes — latest first
 */
export function computeMaStack(closes, price = closes?.[0]) {
  const p = price ?? closes?.[0];
  const ema20 = emaLatest(closes, 20);
  const ema50 = emaLatest(closes, 50);
  const ema200 = emaLatest(closes, 200);
  const s20 = emaSeries(closes, 20);
  const s50 = emaSeries(closes, 50);
  const s200 = emaSeries(closes, 200);

  const rawCross = detectLastEmaCross(closes);
  const lastCross = rawCross
    ? {
        type: rawCross.type,
        date: crossToDateIso(closes, rawCross.barsAgo),
        barsAgo: rawCross.barsAgo,
      }
    : null;

  return {
    ema20,
    ema50,
    ema200,
    pctFrom20: pctFrom(p, ema20),
    pctFrom50: pctFrom(p, ema50),
    pctFrom200: pctFrom(p, ema200),
    slope20: slopePct(s20, 5),
    slope50: slopePct(s50, 5),
    slope200: slopePct(s200, 5),
    lastCross,
    goldenCross: ema50 != null && ema200 != null && ema50 > ema200,
    deathCross: ema50 != null && ema200 != null && ema50 < ema200,
  };
}

/** Weekly EMA40 for long-term lane (40-week ≈ 40 weekly bars). */
export function ema40Weekly(weeklyCloses) {
  return emaLatest(weeklyCloses, 40);
}

export function attachMovingAverages(result) {
  if (!result?.closes?.length) return result;
  const price = result.price ?? result.closes[0];
  const ma = computeMaStack(result.closes, price);
  result.ma = ma;
  result.ema20 = ma.ema20;
  result.ema50 = ma.ema50;
  result.ema200 = ma.ema200;
  result.sm50 = ma.ema50;
  result.sm200 = ma.ema200;
  result.goldenCross = ma.goldenCross;
  result.deathCross = ma.deathCross;
  return result;
}

export function formatMaPill(price, ema, slope) {
  if (ema == null || price == null) return { above: null, arrow: "—" };
  const above = price > ema;
  let arrow = "→";
  if (slope != null) {
    if (slope > 0.15) arrow = "↑";
    else if (slope < -0.15) arrow = "↓";
  }
  return { above, arrow };
}
